import { CHAIN_IDS, HYPERCORE_CHAIN_ID } from '@/constants/chain';

import type {
  GroupedTokenBalance,
  SortColumn,
  TokenBalance,
  WalletChartPoint,
  WalletPortfolio,
} from '@/utils/zerion/types';

import type {
  AddressedWalletFilter,
  PortfolioRow,
  TokenUnrealizedPnL,
  WalletFilter,
  WalletFilterOption,
} from './types';

/** Zerion chart timestamps may be Unix seconds; anything below 1e12 is read as seconds. */
export const toMilliseconds = (timestamp: number) =>
  timestamp < 1e12 ? timestamp * 1000 : timestamp;

/** A millisecond timestamp in the units of `series`, which may be in seconds. */
export const toSeriesUnits = (timestampMs: number, series: WalletChartPoint[]) =>
  series.length > 0 && series[0].timestamp < 1e12 ? timestampMs / 1000 : timestampMs;

type ConnectedWalletOption = Pick<WalletFilterOption, 'wallet'> & { address?: string };

/**
 * Wallet filter options for the connected viewer. `all` only appears when both
 * an EVM and a Solana wallet are connected.
 */
export function buildConnectedWalletOptions(
  evm: ConnectedWalletOption | undefined,
  solana: ConnectedWalletOption | undefined,
): WalletFilterOption[] {
  const options: WalletFilterOption[] = [];
  if (evm?.address && solana?.address) options.push({ id: 'all', label: 'All Wallets' });
  if (evm?.address) {
    options.push({ id: 'evm', label: 'EVM', address: evm.address, wallet: evm.wallet });
  }
  if (solana?.address) {
    options.push({ id: 'solana', label: 'Solana', address: solana.address, wallet: solana.wallet });
  }
  return options;
}

/** Per-wallet totals for the selector, derived from the unfiltered portfolio. */
export function getWalletTotals(
  portfolio: WalletPortfolio | undefined,
): Record<WalletFilter, number> | undefined {
  if (!portfolio) return undefined;
  const solana = portfolio.chainDistribution[CHAIN_IDS.SOLANA] ?? 0;
  return {
    all: portfolio.totalValue,
    evm: portfolio.totalValue - solana,
    solana,
  };
}

/**
 * The wallet menu shows a checkbox per wallet over a single-value filter, where
 * `all` reads as every box checked. Unchecking the last checked box would hide
 * every wallet, so it snaps back to `all` instead.
 */
export function toggleWalletFilter(
  selected: WalletFilter,
  id: AddressedWalletFilter,
): WalletFilter {
  if (selected === 'all') return id === 'evm' ? 'solana' : 'evm';
  return 'all';
}

function getTokenKey(token: TokenBalance): string {
  return `${token.chainId}-${token.tokenAddress}`;
}

const tokenBalanceFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 6,
});

export function formatTokenBalance(value: number): string {
  return tokenBalanceFormatter.format(value);
}

export function getAvgEntryPrice(row: PortfolioRow): number | undefined {
  if (!row.pnlData) return undefined;
  const { costBasis, quantity } = row.pnlData;
  if (quantity <= 0 || costBasis <= 0) return undefined;
  return costBasis / quantity;
}

export function groupTokensByFungible(
  tokens: TokenBalance[],
): (TokenBalance | GroupedTokenBalance)[] {
  const fungibleGroups = new Map<string, TokenBalance[]>();
  const ungroupedTokens: TokenBalance[] = [];

  for (const token of tokens) {
    if (token.fungibleId) {
      const existing = fungibleGroups.get(token.fungibleId) || [];
      existing.push(token);
      fungibleGroups.set(token.fungibleId, existing);
    } else {
      ungroupedTokens.push(token);
    }
  }

  const result: (TokenBalance | GroupedTokenBalance)[] = [];

  for (const [fungibleId, positions] of fungibleGroups) {
    if (positions.length === 1) {
      result.push(positions[0]);
    } else {
      // primaryPosition is highest-value for group display
      const sortedPositions = [...positions].sort(
        (a, b) => (b.balanceUsd ?? 0) - (a.balanceUsd ?? 0),
      );
      const primaryPosition = sortedPositions[0];

      const totalBalanceUsd = positions.reduce((sum, p) => sum + (p.balanceUsd ?? 0), 0);

      result.push({
        fungibleId,
        name: primaryPosition.name,
        symbol: primaryPosition.symbol,
        logo: primaryPosition.logo,
        priceUsd: primaryPosition.priceUsd,
        priceChangePercent1d: primaryPosition.priceChangePercent1d,
        priceChangeAbsolute1d: primaryPosition.priceChangeAbsolute1d,
        totalBalanceUsd,
        positions: sortedPositions,
      });
    }
  }

  result.push(...ungroupedTokens);

  result.sort((a, b) => {
    const aValue = 'totalBalanceUsd' in a ? a.totalBalanceUsd : (a.balanceUsd ?? 0);
    const bValue = 'totalBalanceUsd' in b ? b.totalBalanceUsd : (b.balanceUsd ?? 0);
    return bValue - aValue;
  });

  return result;
}

/** Zerion PnL batches; HyperCore rows carry their own cost basis instead. */
export function buildPnLTokenBlocks(tokens: TokenBalance[]) {
  return groupTokensByFungible(
    tokens.filter((token) => token.priceUsd != null && token.chainId !== HYPERCORE_CHAIN_ID),
  ).map((item) => {
    const positions = isGroupedToken(item) ? item.positions : [item];
    return positions.map(({ chainId, tokenAddress }) => ({ chainId, tokenAddress }));
  });
}

export function isGroupedToken(
  token: TokenBalance | GroupedTokenBalance,
): token is GroupedTokenBalance {
  return 'positions' in token && Array.isArray(token.positions);
}

const SORT_COLUMNS: readonly SortColumn[] = [
  'value',
  'unrealizedReturn',
  'unrealizedPnl',
  'allocation',
];

export function isSortColumn(id: string): id is SortColumn {
  return (SORT_COLUMNS as readonly string[]).includes(id);
}

function computePnlData(
  token: TokenBalance,
  getPnLForToken: (token: TokenBalance) => TokenUnrealizedPnL | undefined,
): PortfolioRow['pnlData'] {
  const pnl = getPnLForToken(token);
  if (!pnl || pnl.unrealizedGain === 0) return null;
  const value = pnl.unrealizedGain;
  const costBasis = (token.balanceUsd ?? 0) - value;
  const percentage = costBasis > 0 ? (value / costBasis) * 100 : 0;
  return { unrealizedGain: value, percentage, costBasis, quantity: tokenQuantity(token) };
}

const tokenQuantity = (token: TokenBalance) => parseFloat(token.balanceFormatted || '0');

function computeGroupPnlData(
  positions: TokenBalance[],
  getPnLForToken: (token: TokenBalance) => TokenUnrealizedPnL | undefined,
): PortfolioRow['pnlData'] {
  let unrealizedGain = 0;
  let totalCostBasis = 0;
  // Only positions with PnL data contribute cost basis, so only their quantity can be
  // averaged against it; a position without a source would dilute the entry price.
  let quantity = 0;
  let hasAnyPnL = false;

  for (const position of positions) {
    const pnl = getPnLForToken(position);
    if (pnl) {
      hasAnyPnL = true;
      unrealizedGain += pnl.unrealizedGain;
      const costBasis = (position.balanceUsd ?? 0) - pnl.unrealizedGain;
      totalCostBasis += costBasis;
      quantity += tokenQuantity(position);
    }
  }

  if (!hasAnyPnL || unrealizedGain === 0) return null;
  const percentage = totalCostBasis > 0 ? (unrealizedGain / totalCostBasis) * 100 : 0;
  return { unrealizedGain, percentage, costBasis: totalCostBasis, quantity };
}

/**
 * Build flat PortfolioRow[] with sub-rows for grouped tokens.
 * Converts raw data from hooks into the TanStack-compatible format.
 */
export function buildPortfolioRows(params: {
  tokens: (TokenBalance | GroupedTokenBalance)[];
  getPnLForToken: (token: TokenBalance) => TokenUnrealizedPnL | undefined;
}): PortfolioRow[] {
  const { tokens, getPnLForToken } = params;

  return tokens.map((item): PortfolioRow => {
    if (isGroupedToken(item)) {
      const primaryPosition = item.positions[0];
      const subRows: PortfolioRow[] = item.positions.map((position) => ({
        key: getTokenKey(position),
        token: position,
        pnlData: computePnlData(position, getPnLForToken),
      }));

      return {
        key: `group-${item.fungibleId}`,
        token: primaryPosition,
        group: item,
        pnlData: computeGroupPnlData(item.positions, getPnLForToken),
        subRows,
      };
    }

    return {
      key: getTokenKey(item),
      token: item,
      pnlData: computePnlData(item, getPnLForToken),
    };
  });
}
